import { useEffect, useMemo, useState } from 'react';
import { Mail, Send, X } from 'lucide-react';
import { api } from '../lib/api';

const post=body=>({method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(body)});

export default function EmailComposeDialog({mailboxes=[],customerId='',defaultTo='',close,onSent}){
  const[mailboxId,setMailboxId]=useState(mailboxes[0]?.id||'');
  const[to,setTo]=useState(defaultTo||'');
  const[cc,setCc]=useState('');
  const[bcc,setBcc]=useState('');
  const[templates,setTemplates]=useState([]);
  const[templateKey,setTemplateKey]=useState('');
  const[subject,setSubject]=useState('');
  const[body,setBody]=useState('');
  const[showCopies,setShowCopies]=useState(false);
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState('');

  useEffect(()=>{if(!mailboxId&&mailboxes[0]?.id)setMailboxId(mailboxes[0].id);},[mailboxes,mailboxId]);
  useEffect(()=>{let active=true;api('/api/communications/templates?channel=email&status=active').then(data=>{if(!active)return;const rows=(data.templates||[]).filter(x=>Number(x.direct_send_enabled)!==0);setTemplates(rows);const preferred=rows.find(x=>x.template_key==='crm_staff_email')||rows[0];if(preferred)setTemplateKey(preferred.template_key)}).catch(()=>{});return()=>{active=false}},[]);
  useEffect(()=>{
    if(defaultTo){setTo(defaultTo);return}
    if(!customerId)return;
    let active=true;
    api('/api/customers/'+encodeURIComponent(customerId)).then(data=>{
      if(!active)return;
      const customer=data?.customer||{};
      const contacts=data?.contacts||[];
      const verified=customer.verified_email||contacts.find(x=>x.contact_type==='email'&&String(x.verification_status||'').toLowerCase()==='verified')?.contact_value||'';
      if(verified)setTo(verified);
    }).catch(()=>{});
    return()=>{active=false};
  },[customerId,defaultTo]);

  const selectedMailbox=useMemo(()=>mailboxes.find(x=>x.id===mailboxId)||null,[mailboxes,mailboxId]);

  const submit=async event=>{
    event.preventDefault();
    setBusy(true);setNotice('');
    try{
      const result=await api('/api/email/send',post({
        mailbox_id:mailboxId,
        to,cc,bcc,subject,body,template_key:templateKey,
        customer_id:customerId||undefined,
      }));
      setNotice('Email sent through Microsoft 365.');
      onSent?.(result);
      setTimeout(()=>close?.(),450);
    }catch(error){setNotice('Error: '+error.message)}
    finally{setBusy(false)}
  };

  return <div className="modal-backdrop crm-modal" onMouseDown={e=>e.target===e.currentTarget&&!busy&&close?.()}>
    <form className="crm-dialog email-compose-dialog" onSubmit={submit}>
      <div className="crm-dialog-head">
        <div><span className="dialog-eyebrow">Microsoft 365 · Customer Operations</span><h2 className="govuk-heading-m">New email</h2><p className="govuk-body">Every CRM email is rendered from an approved active Template Library version before Microsoft 365 sends it.</p></div>
        <button type="button" className="icon-button" onClick={close} disabled={busy}><X size={18}/></button>
      </div>
      <div className="crm-dialog-body">
        {notice&&<div className={'email-inline-notice '+(notice.startsWith('Error:')?'error':'')}>{notice}</div>}
        <div className="email-compose-grid">
          <label className="govuk-label"><span>From</span><select className="govuk-select" required value={mailboxId} onChange={e=>setMailboxId(e.target.value)}><option value="">Choose mailbox…</option>{mailboxes.map(box=><option key={box.id} value={box.id}>{box.display_name||box.email} · {box.email}</option>)}</select></label>
          <label className="govuk-label"><span>Template</span><select className="govuk-select" required value={templateKey} onChange={e=>setTemplateKey(e.target.value)}><option value="">Choose approved email template…</option>{templates.map(t=><option key={t.template_key} value={t.template_key}>{t.name} · v{t.version_number||'—'}</option>)}</select></label>
          <label className="govuk-label"><span>To</span><input className="govuk-input" required value={to} onChange={e=>setTo(e.target.value)} placeholder="customer@example.com"/></label>
          <div className="email-copy-toggle"><button type="button" onClick={()=>setShowCopies(v=>!v)}>{showCopies?'Hide':'Add'} CC / BCC</button></div>
          {showCopies&&<><label className="govuk-label"><span>CC</span><input className="govuk-input" value={cc} onChange={e=>setCc(e.target.value)} placeholder="Separate addresses with commas"/></label><label className="govuk-label"><span>BCC</span><input className="govuk-input" value={bcc} onChange={e=>setBcc(e.target.value)} placeholder="Separate addresses with commas"/></label></>}
          <label className="govuk-label"><span>Subject</span><input className="govuk-input" required maxLength="1000" value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Email subject"/></label>
          <label className="govuk-label"><span>Message</span><textarea className="govuk-textarea" required rows="12" maxLength="30000" value={body} onChange={e=>setBody(e.target.value)} placeholder="Write your email…"/></label>
        </div>
        {selectedMailbox&&<div className="email-compose-footnote"><Mail size={14}/>Sending from <strong>{selectedMailbox.email}</strong>. Template Library controls the final subject/body shell; Microsoft 365 retains the sent item and syncs it back into Customer Operations.</div>}
      </div>
      <div className="crm-dialog-footer"><button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={close} disabled={busy}>Cancel</button><button className="button primary govuk-button" disabled={busy||!mailboxId||!templateKey||!to.trim()||!subject.trim()||!body.trim()}><Send size={15}/>{busy?'Sending…':'Send email'}</button></div>
    </form>
  </div>;
}
